import React, { useState } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FinancialTransparency = ({ financialData }) => {
  const [activeTab, setActiveTab] = useState('allocation');

  const COLORS = {
    programs: '#2563EB',
    administrative: '#059669',
    fundraising: '#F59E0B',
    reserves: '#6B7280'
  };

  const tabs = [
    { id: 'allocation', label: 'Fund Allocation', icon: 'PieChart' },
    { id: 'revenue', label: 'Revenue Trends', icon: 'TrendingUp' },
    { id: 'reports', label: 'Financial Reports', icon: 'FileText' }
  ];

  const renderAllocationChart = () => (
    <div className="space-y-6">
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={financialData?.allocation}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={2}
              dataKey="value"
            >
              {financialData?.allocation?.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS?.[entry?.category]} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => `${value}%`} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {financialData?.allocation?.map((item) => (
          <div key={item?.category} className="flex items-center space-x-3">
            <div
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: COLORS?.[item?.category] }}
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-card-foreground capitalize">
                {item?.category?.replace(/([A-Z])/g, ' $1')?.trim()}
              </div>
              <div className="text-xs text-muted-foreground">{item?.value}%</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderRevenueChart = () => (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={financialData?.revenueHistory}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis dataKey="year" stroke="#6B7280" />
          <YAxis stroke="#6B7280" />
          <Tooltip formatter={(value) => [`$${value?.toLocaleString()}`, 'Revenue']} />
          <Bar dataKey="amount" fill="#2563EB" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  const renderReports = () => (
    <div className="space-y-4">
      {financialData?.reports?.map((report) => (
        <div key={report?.id} className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-micro">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={20} className="text-primary" />
            </div>
            <div>
              <div className="font-medium text-card-foreground">{report?.title}</div>
              <div className="text-sm text-muted-foreground">{report?.year} • {report?.pages} pages</div>
            </div>
          </div>
          <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
            Download
          </Button>
        </div>
      ))}
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="Shield" size={24} className="text-primary" />
        <h2 className="text-xl font-semibold text-card-foreground">Financial Transparency</h2>
      </div>
      {/* Transparency Score */}
      <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-2xl font-bold text-success">{financialData?.transparencyScore}%</div>
            <div className="text-sm text-success">Transparency Score</div>
          </div>
          <div className="text-right">
            <div className="text-sm font-medium text-card-foreground">Excellent Rating</div>
            <div className="text-xs text-muted-foreground">Based on financial disclosure</div>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex space-x-1 mb-6 bg-muted rounded-lg p-1">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-micro ${
              activeTab === tab?.id
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tab?.icon} size={16} />
            <span className="hidden sm:inline">{tab?.label}</span>
          </button>
        ))}
      </div>
      {/* Tab Content */}
      <div>
        {activeTab === 'allocation' && renderAllocationChart()}
        {activeTab === 'revenue' && renderRevenueChart()}
        {activeTab === 'reports' && renderReports()}
      </div>
    </div>
  );
};

export default FinancialTransparency;